import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { LanguageContext, translate } from "@/i18n";
import { LoginGate } from "@/components/LoginGate";
import { NdaBanner } from "@/components/NdaBanner";
import { ImageLightbox } from "@/components/ImageLightbox";
import { Sidebar } from "@/components/Sidebar";
import { Header } from "@/components/Header";
import { CatalogHome } from "@/components/CatalogHome";
import { ProductDetail } from "@/components/ProductDetail";
import { ProductPage } from "@/components/ProductPage";
import { CountryExamples } from "@/components/CountryExamples";
import { DigitalRepository } from "@/components/DigitalRepository";
import { BUOwnerDeck } from "@/components/BUOwnerDeck";
import { Onboarding } from "@/components/Onboarding";
import { CountrySetup } from "@/components/setup/CountrySetup";
import { Submissions } from "@/components/Submissions";
import { Contacts } from "@/components/Contacts";
import { CountryDashboard } from "@/components/CountryDashboard";
import { Legal } from "@/components/Legal";
import { buildUserProfile } from "@/data/accessCodes";

const PROFILE_KEY = "fora_profile";
const LANG_KEY = "fora_lang";

const readProfile = () => {
  try {
    const raw = sessionStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

function App() {
  const [profile, setProfile] = useState(() => readProfile());
  const authenticated = !!profile;
  const [activeView, setActiveView] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLangState] = useState(
    () => sessionStorage.getItem(LANG_KEY) || readProfile()?.defaultLang || "en"
  );

  const setLang = useCallback((l) => {
    sessionStorage.setItem(LANG_KEY, l);
    setLangState(l);
  }, []);

  const t = useCallback((s) => translate(lang, s), [lang]);

  const navigate = (view) => {
    // Handle "home#section-id" → navigate home + scroll (track anchor for sidebar highlight)
    if (view.includes("#")) {
      setActiveView(view);
      setSidebarOpen(false);
      const anchor = view.split("#")[1];
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${anchor}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    setActiveView(view);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Allow footer (and other deep components) to navigate without prop drilling.
  useEffect(() => {
    const handler = (e) => navigate(e.detail);
    window.addEventListener("fora-navigate", handler);
    return () => window.removeEventListener("fora-navigate", handler);
  }, []);

  const handleAuth = (code) => {
    const next = buildUserProfile(code);
    if (!next) return;
    sessionStorage.setItem(PROFILE_KEY, JSON.stringify(next));
    // Legacy key (kept for any older check); harmless.
    sessionStorage.setItem("fora_auth", "1");
    // Set default language from the profile if the user hasn't chosen one yet.
    if (!sessionStorage.getItem(LANG_KEY)) {
      setLang(next.defaultLang || "en");
    }
    setProfile(next);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PROFILE_KEY);
    sessionStorage.removeItem("fora_auth");
    setProfile(null);
    setActiveView("home");
  };

  if (!authenticated) {
    return (
      <LanguageContext.Provider value={{ lang, setLang, t }}>
        <LoginGate onAuth={handleAuth} />
      </LanguageContext.Provider>
    );
  }

  const renderView = () => {
    // Strip anchor for routing (e.g. "home#category-section-brand-activation" → "home")
    const baseView = activeView.split("#")[0];
    // Dynamic product pages: "product:<id>"
    if (baseView.startsWith("product:")) {
      const productId = baseView.split(":")[1];
      return <ProductPage productId={productId} onNavigate={navigate} />;
    }
    switch (baseView) {
      case "home":
        return <CatalogHome onNavigate={navigate} />;
      case "product":
        return <ProductDetail onNavigate={navigate} />;
      case "countries":
        return <CountryExamples onNavigate={navigate} />;
      case "repository":
        return <DigitalRepository onNavigate={navigate} />;
      case "data":
        return <BUOwnerDeck onNavigate={navigate} />;
      case "onboarding":
        return <Onboarding onNavigate={navigate} />;
      case "setup":
        return <CountrySetup onNavigate={navigate} />;
      case "submissions":
        return <Submissions />;
      case "contacts":
        return <Contacts onNavigate={navigate} />;
      case "legal":
        return <Legal />;
      case "country-dashboard":
        // Guard: only render if the logged-in profile owns a dashboard.
        if (profile?.dashboardUrl) {
          return <CountryDashboard profile={profile} />;
        }
        return <CatalogHome onNavigate={navigate} />;
      default:
        return <CatalogHome onNavigate={navigate} />;
    }
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
    <div className="flex h-screen overflow-hidden" data-testid="app-root">
      <NdaBanner />
      <ImageLightbox />
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <Sidebar activeView={activeView} onNavigate={navigate} profile={profile} />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          activeView={activeView}
          onNavigate={navigate}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onLogout={handleLogout}
        />
        <main className="flex-1 overflow-y-auto bg-[#fafafa]">
          <div className="p-4 sm:p-6 lg:p-10 max-w-[1200px]">
            {renderView()}
          </div>
        </main>
      </div>
    </div>
    </LanguageContext.Provider>
  );
}

export default App;
